import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { TextButton } from '@components/core-ui/form-elements/buttons/Button/variations/TextButton';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { ModalFooterEnd } from '@modals/core-ui/ModalFooter/variations/ModalFooterEnd';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { useAPI } from '@providers/useAPI';
import { useQuery } from '@tanstack/react-query';
import { PrismaRepositoryAttachmentDto } from 'app/API';
import { createFetchRepositoryAudioAttachmentsQueryFn } from 'app/queries/repositories/createFetchRepositoryAudioAttachmentsQueryFn';
import { repositoriesKeys } from 'app/queries/repositories/repositoriesKeys';
import { useState } from 'react';
import { Content } from './styles/Content';
import { TProps } from './types/TProps';
import { CheckBox } from '@components/core-ui/form-elements/CheckBox';

export const SelectCaseFiles = ({ repositoryId, onCancel, onSuccess }: TProps) => {
  const { call } = useAPI();
  const [selectedFiles, setSelectedFiles] = useState<PrismaRepositoryAttachmentDto[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const attachmentsQuery = useQuery({
    queryKey: repositoriesKeys.audioAttachmentsByRepository(repositoryId),
    queryFn: createFetchRepositoryAudioAttachmentsQueryFn(call),
  });
  const files = attachmentsQuery.data ?? [];
  const isLoadingAttachments = attachmentsQuery.isLoading;
  const hasLoadError = attachmentsQuery.isError;

  const changeSelectedFiles = (checked: boolean, f: PrismaRepositoryAttachmentDto) => {
    setSelectedFiles(
      checked ? [...selectedFiles, f] : selectedFiles.filter((selectedFile) => selectedFile.id !== f.id),
    );
  };

  const handleSubmit = async () => {
    if (isSubmitting || isLoadingAttachments) {
      return;
    }
    setIsSubmitting(true);
    try {
      await onSuccess(selectedFiles);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isBusy = isLoadingAttachments || isSubmitting;

  return (
    <ModalWrapper width="487px">
      {isBusy ? <AbsoluteSpinner overlay={true} /> : null}
      <ModalTitle>Select audio files</ModalTitle>
      <Content>
        {files.length ? (
          <ul className="max-h-[50vh] divide-y divide-border overflow-y-auto">
            {files.map((data) => (
              <li key={data.id} className="flex items-center py-4 last-of-type:pb-0 first-of-type:pt-0">
                <CheckBox
                  name={data.fileName}
                  onChange={(state) => changeSelectedFiles(state, data)}
                  checked={!!selectedFiles.find((selectedFile) => selectedFile.id === data.id)}
                >
                  {data.fileName}
                </CheckBox>
              </li>
            ))}
          </ul>
        ) : hasLoadError ? (
          <div className="flex items-center gap-1 py-4 justify-center">
            <p className="m-0 text-sm">Unable to load audio files from this case.</p>
            <TextButton
              className="!px-0"
              onClick={() => void attachmentsQuery.refetch()}
              disabled={isLoadingAttachments}
            >
              Try again
            </TextButton>
          </div>
        ) : (
          <p className="text-sm py-4 text-center">{isLoadingAttachments ? '' : 'No audio files available'}</p>
        )}

        <ModalFooterEnd>
          <OutlinedButton onClick={onCancel} disabled={isBusy}>
            Cancel
          </OutlinedButton>
          <Button onClick={handleSubmit} disabled={isBusy || !selectedFiles.length}>
            Import files
          </Button>
        </ModalFooterEnd>
      </Content>
    </ModalWrapper>
  );
};
